import React, { ReactElement } from "react"
import { Collapse } from "react-collapse"
import styled from "styled-components"
import "./block.css"
import { useLocalState } from "../../utils/react"

const Div = styled.div``

interface BadBlockContentProps {
  content: string
}

export interface BlockProps extends React.HTMLAttributes<HTMLDivElement> {
  content: ReactElement
  referenceMode: boolean
}

const classNames = (...names: Array<string | boolean | undefined>) =>
  names.filter(it => it).join(" ")

export const Block = ({ content, children, className, referenceMode = false, ...restProps }: BlockProps) => {
  const [opened, setOpened] = useLocalState(`${restProps.id}.opened`, true)

  const toggleBlock = () => {
    if (children) setOpened(!opened)
  }

  const BlockControls = () => <div className={classNames(
    CSS.blockControls,
    !children && CSS.blockHasNoChildren,
    !opened && CSS.blockClosed)}
  >
    <div className={CSS.blockBullet} onClick={toggleBlock} />
    <div className={CSS.blockControlbar} onClick={toggleBlock} />
  </div>

  const referenceModeCss = referenceMode ? "display:inline" : ""

  return <Div className={classNames(CSS.block, className)} css={referenceModeCss} {...restProps}>
    {!referenceMode && <BlockControls />}

    <Div className={CSS.blockBody} css={referenceModeCss}>
      <BlockContent css={referenceModeCss}>
        {content}
      </BlockContent>

      {children && !referenceMode && <BlockChildren>
        <Collapse isOpened={opened!}>
          {React.Children.map(children, child => <ChildBlock>{child}</ChildBlock>)}
        </Collapse>
      </BlockChildren>}
    </Div>
  </Div>
}

export const BlockReference = ({ referenceMode = true, ...restProps }: BlockProps) =>
  <Block referenceMode={referenceMode} {...restProps}/>

export const BlockContent = (props: React.HTMLAttributes<HTMLDivElement>) =>
  <Div className={CSS.blockContent} {...props}>
    {props.children}
  </Div>

export const BlockChildren = (props: React.HTMLAttributes<HTMLDivElement>) =>
  <div className={CSS.blockChildren} {...props}>
    {props.children}
  </div>

export const ChildBlock = (props: React.HTMLAttributes<HTMLDivElement>) =>
  <div className={CSS.childBlock} {...props}>
    {props.children}
  </div>

export const BlockContentFailedParsing = ({ content }: BadBlockContentProps) =>
  <span className={CSS.blockFailedParsing}
        title={"This block failed to parse and is displayed as plaintext"}>
  {`${content}`}
</span>


const CSS = {
  block: "block",
  childBlock: "child-block",
  blockBody: "block-body",
  blockContent: "block-content",
  blockChildren: "block-children",

  blockControls: "block-controls",
  blockBullet: "block-bullet",
  blockControlbar: "block-controlbar",


  blockFailedParsing: "block-failed-parsing",
  blockHasNoChildren: "block-has-no-children",
  blockClosed: "block-closed",
}
